import { isArray, isNil } from 'lodash-es'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  TSearchFilter,
  useSearchFilterStore,
  defaultFilters,
  RoomDiscount,
  PopularOption,
  Floor,
  PyeongSize,
  RoomCount,
} from '@/entities/map'
import { PropertyType, BasicOption, AdditionalOption } from '@/entities/room'

export enum Filter {
  RoomCounts = 'roomCounts',
  UsingFee = 'usingFee',
  PropertyTypes = 'propertyTypes',
  PyeongSizes = 'pyeongSizes',
  Floors = 'floors',
  Options = 'options',
  RoomDiscounts = 'roomDiscounts',
}

type FilterValue =
  | RoomCount
  | PropertyType
  | PyeongSize
  | Floor
  | PopularOption
  | BasicOption
  | AdditionalOption
  | RoomDiscount
  | number
  | null

type FilterState = {
  filter: TSearchFilter
  updateFilter: (newFilters: {
    [key in keyof TSearchFilter]?: FilterValue
  }) => void
  resetFilter: () => void
}

const FilterContext = createContext<FilterState | null>(null)

export const FilterProvider = ({ children }: PropsWithChildren) => {
  const filterState = useSearchFilterStore(state => state.filters)

  const [filter, setFilter] = useState<TSearchFilter>(filterState)

  const updateFilter = useCallback(
    (newFilters: { [key in keyof TSearchFilter]?: FilterValue }) => {
      setFilter(prevFilter => {
        const updatedFilter = Object.entries(newFilters).map(([key, value]) => {
          const prevValue = prevFilter[key as keyof TSearchFilter]

          if (!isArray(prevValue)) {
            return [key, value]
          }

          if (!isNil(value) && prevValue.includes(value as never)) {
            return [key, prevValue.filter(item => item !== value)]
          }

          return [key, [...prevValue, value]]
        })

        return {
          ...prevFilter,
          ...(Object.fromEntries(updatedFilter) as Partial<TSearchFilter>),
        }
      })
    },
    [setFilter],
  )

  const resetFilter = useCallback(() => {
    setFilter(defaultFilters)
  }, [setFilter])

  const value = useMemo(
    () => ({ filter, updateFilter, resetFilter }),
    [filter, updateFilter, resetFilter],
  )

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  )
}

export const useFilter = () => {
  const context = useContext(FilterContext)

  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider')
  }

  return context
}
